import { QuestionDefinition, QuestionType } from '../types/survey.types';
import mammoth from 'mammoth';

export interface ParsedQuestion {
  text: string;
  type: QuestionType;
  required: boolean;
  options?: string[];
  detectedFrom?: string;
  confidence?: 'high' | 'medium' | 'low';
}

// CSV Parser
export const parseCSV = (csvText: string): ParsedQuestion[] => {
  const lines = csvText.split('\n');
  const questions: ParsedQuestion[] = [];

  // Skip header row and template rows
  let startIndex = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes('START YOUR SURVEY BELOW') ||
        lines[i].includes('EXAMPLE QUESTIONS')) {
      startIndex = i + 1;
    }
  }

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('Question Text') || line.startsWith('TEMPLATE') ||
        line.startsWith('NOTES') || line.startsWith('EXAMPLE') || line.startsWith('-')) {
      continue;
    }

    const columns = parseCSVLine(line);
    if (columns.length >= 3 && columns[0] && columns[1]) {
      const questionText = columns[0];
      const questionType = columns[1].toLowerCase() as QuestionType;
      const required = columns[2]?.toUpperCase() === 'Y';
      const options = columns[3] ? columns[3].split('|').map(opt => opt.trim()).filter(Boolean) : undefined;

      questions.push({
        text: questionText,
        type: questionType,
        required,
        options,
        detectedFrom: 'csv',
        confidence: 'high'
      });
    }
  }

  return questions;
};

// Helper to parse CSV line (handles quoted values)
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

// DOCX Parser (uses mammoth to extract text from .docx files)
export const parseDOCX = async (file: File): Promise<ParsedQuestion[]> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    const text = result.value;

    // Now parse the extracted text using the document parser
    return parseDocument(text);
  } catch (error) {
    console.error('Error parsing DOCX file:', error);
    throw new Error('Failed to parse DOCX file. Please ensure it is a valid Word document.');
  }
};

// Document Parser (Pattern Matching - NO AI COST)
export const parseDocument = (docText: string): ParsedQuestion[] => {
  const lines = docText.split('\n').map(l => l.trim()).filter(Boolean);
  const questions: ParsedQuestion[] = [];

  let currentQuestion: ParsedQuestion | null = null;
  let options: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Skip title/header lines
    if (i < 3 && (line.includes('Survey') || line.includes('Quality Assurance'))) {
      continue;
    }

    // Skip instruction paragraphs
    if (line.length > 100 && !line.includes('?') && !line.includes('*')) {
      continue;
    }

    // Detect option lines (start with *)
    if (line.startsWith('*')) {
      const optionText = line.substring(1).trim();

      // Check if it has a text input (for "Yes (please list)" scenarios)
      if (optionText.includes(':') || optionText.includes('__')) {
        options.push(optionText.split(':')[0].split('__')[0].trim());
      } else {
        options.push(optionText);
      }
      continue;
    }

    // If we have collected options, save the previous question
    if (currentQuestion && options.length > 0) {
      currentQuestion.options = options;
      currentQuestion.type = 'radio';
      currentQuestion.confidence = 'high';
      questions.push(currentQuestion);
      currentQuestion = null;
      options = [];
    }

    // Detect questions
    if (line.includes('?')) {
      // Save previous question if exists
      if (currentQuestion) {
        questions.push(currentQuestion);
      }

      const questionType = detectQuestionType(line, lines[i + 1] || '');
      currentQuestion = {
        text: line,
        type: questionType.type,
        required: questionType.required,
        detectedFrom: questionType.reason,
        confidence: questionType.confidence
      };
    }
    // Detect input fields (lines with underscores)
    else if (line.includes('___')) {
      const parts = line.split('___');
      const questionText = parts[0].trim();

      if (questionText) {
        const questionType = detectQuestionType(questionText, '');
        questions.push({
          text: questionText.endsWith(':') ? questionText : questionText + ':',
          type: questionType.type,
          required: questionType.required,
          detectedFrom: questionType.reason,
          confidence: questionType.confidence
        });
      }
    }
  }

  // Add last question if exists
  if (currentQuestion) {
    if (options.length > 0) {
      currentQuestion.options = options;
      currentQuestion.type = 'radio';
      currentQuestion.confidence = 'high';
    }
    questions.push(currentQuestion);
  }

  return questions;
};

// Detect question type using pattern matching
const detectQuestionType = (questionText: string, nextLine: string): {
  type: QuestionType;
  required: boolean;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
} => {
  const lower = questionText.toLowerCase();

  // Email detection
  if (lower.includes('email') || lower.includes('e-mail')) {
    return { type: 'email', required: true, reason: 'Contains "email"', confidence: 'high' };
  }

  // Phone detection
  if (lower.includes('phone') || lower.includes('mobile') || lower.includes('contact number')) {
    return { type: 'phone', required: false, reason: 'Contains "phone"', confidence: 'high' };
  }

  // Date detection
  if (lower.includes('date') || lower.includes('when did')) {
    return { type: 'date', required: false, reason: 'Contains "date"', confidence: 'high' };
  }

  // Number detection
  if (lower.includes('how long') || lower.includes('how many') || lower.includes('days') ||
      lower.includes('number') || lower.includes('age') || lower.includes('dosage')) {
    return { type: 'number', required: false, reason: 'Numeric question', confidence: 'high' };
  }

  // Rating/Satisfaction detection
  if (lower.includes('rate') || lower.includes('rating')) {
    return { type: 'rating', required: true, reason: 'Contains "rate"', confidence: 'high' };
  }

  if (lower.includes('satisfied') || lower.includes('satisfaction')) {
    return { type: 'radio', required: true, reason: 'Satisfaction scale', confidence: 'high' };
  }

  if (lower.includes('effective')) {
    return { type: 'radio', required: true, reason: 'Effectiveness scale', confidence: 'high' };
  }

  // Yes/No detection
  if (nextLine.toLowerCase().includes('yes') || nextLine.toLowerCase().includes('no')) {
    return { type: 'radio', required: true, reason: 'Yes/No question', confidence: 'high' };
  }

  // Comments/Other/Explain = text area
  if (lower.includes('comment') || lower.includes('other') || lower.includes('explain') ||
      lower.includes('describe') || lower.includes('additional')) {
    return { type: 'textarea', required: false, reason: 'Open-ended question', confidence: 'high' };
  }

  // Name/initials
  if (lower.includes('name') || lower.includes('initial')) {
    return { type: 'text', required: true, reason: 'Name field', confidence: 'high' };
  }

  // Product/item selection
  if (lower.includes('product') || lower.includes('item') || lower.includes('which')) {
    return { type: 'text', required: false, reason: 'Product field', confidence: 'medium' };
  }

  // Default: short text for questions, textarea for longer prompts
  if (questionText.length > 60) {
    return { type: 'textarea', required: false, reason: 'Long question text', confidence: 'medium' };
  }

  return { type: 'text', required: false, reason: 'Default text input', confidence: 'low' };
};

// Convert parsed questions to QuestionDefinition format
export const convertToQuestionDefinitions = (parsedQuestions: ParsedQuestion[]): Partial<QuestionDefinition>[] => {
  return parsedQuestions.map((pq, index) => {
    // Convert string options to QuestionOption objects
    const options = pq.options?.map((optionText, optIndex) => ({
      id: `option-${Date.now()}-${index}-${optIndex}`,
      label: optionText,
      value: optionText.toLowerCase().replace(/\s+/g, '-'),
      isOther: false
    }));

    return {
      id: `imported-${Date.now()}-${index}`,
      type: pq.type,
      title: pq.text, // QuestionDefinition uses 'title' not 'text'
      required: pq.required,
      options: options,
      order: index,
      validationRules: []
    };
  });
};
